import { useState, useEffect, cloneElement } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  AppBar, Box, CssBaseline, Drawer, IconButton, List, ListItem, ListItemIcon,
  ListItemText, Toolbar, Typography, useTheme, useMediaQuery, Menu, MenuItem,
  Avatar, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Collapse, Tooltip, Chip,
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard as DashboardIcon, Home as HomeIcon, People as PeopleIcon,
  Settings as SettingsIcon, ExitToApp as LogoutIcon, Person as PersonIcon,
  ViewModule as TemplatesIcon, HomeWork as HomeWorkIcon, Business as BusinessIcon,
  Group as GroupIcon, HowToReg as HowToRegIcon, Build as BuildIcon, Event as CalendarIcon,
  CardMembership as SubscriptionIcon, CameraAlt as CameraIcon, Link as LinkIcon,
  Campaign as CampaignIcon, Assessment as AssessmentIcon, AttachMoney as AttachMoneyIcon,
  ContactSupport as SupportIcon, ExpandLess, ExpandMore, Receipt as ReceiptIcon,
  ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useFeatures } from '../hooks/useFeatures';
import NotificationBell from '../components/NotificationBell';
import PropertyAssistantChat from '../components/PropertyAssistantChat';
import { hasAdminAccess } from '../utils/roleUtils';
import { palette, FONT_DISPLAY, FONT_MONO } from '../theme';
import BrandMark from '../components/BrandMark';

const drawerWidth = 248;
const miniWidth = 72;
const SIDEBAR_KEY = 'wf:sidebarCollapsed';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout = ({ children }: MainLayoutProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const { user, agency, loading, logout } = useAuth();
  const { menuStructure, loading: featuresLoading } = useFeatures();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Sidebar: click-to-toggle + persisted in localStorage. Replaces the
  // hover-collapse behaviour, which was glitchy on multi-monitor / trackpad.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, sidebarCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!loading && !agency?.slug && !agencySlug) navigate('/login');
  }, [loading, agency?.slug, agencySlug, navigate]);

  useEffect(() => {
    if (!agencySlug || !location.pathname) return;
    const base = `/agencies/${agencySlug}/`;
    const path = location.pathname;
    const onCommission =
      path === `${base}commission-report` ||
      path === `${base}commission-performance` ||
      path === `${base}commission-sharing-policy` ||
      path === `${base}cost-items`;
    if (onCommission) setExpandedMenus(prev => prev.Commission ? prev : { ...prev, Commission: true });
  }, [agencySlug, location.pathname]);

  const handleDrawerToggle = () => {
    if (isMobile) setMobileOpen(!mobileOpen);
    else setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleProfileMenuOpen = (e: React.MouseEvent<HTMLElement>) => setProfileMenuAnchor(e.currentTarget);
  const handleProfileMenuClose = () => setProfileMenuAnchor(null);
  const handleProfileClick = () => { handleProfileMenuClose(); navigate(`/agencies/${agencySlug}/profile`); };

  const iconMap: Record<string, React.ReactElement> = {
    Link: <LinkIcon />, Dashboard: <DashboardIcon />, Assessment: <AssessmentIcon />,
    Home: <HomeIcon />, People: <PeopleIcon />, Settings: <SettingsIcon />,
    Description: <TemplatesIcon />, Business: <BusinessIcon />, CalendarToday: <CalendarIcon />,
    Event: <CalendarIcon />, Gavel: <CalendarIcon />, CameraAlt: <CameraIcon />,
    HomeWork: <HomeWorkIcon />, CardMembership: <SubscriptionIcon />, Campaign: <CampaignIcon />,
    HomeRepairService: <HomeWorkIcon />, Workflow: <TemplatesIcon />,
  };

  const handleMenuToggle = (k: string) => setExpandedMenus(p => ({ ...p, [k]: !p[k] }));

  const getIconForMenuItem = (iconName?: string, item?: any) => {
    const label = (item?.label || item?.path || '').toString().toLowerCase();
    if (label.includes('vendor')) return <BusinessIcon />;
    if (label.includes('team')) return <GroupIcon />;
    if (label.includes('attendee')) return <HowToRegIcon />;
    if (label.includes('contractor')) return <BuildIcon />;
    if (label.includes('commission')) return <AttachMoneyIcon />;
    if (label.includes('cost items')) return <ReceiptIcon />;
    if (!iconName) return <HomeIcon />;
    return iconMap[iconName] || <HomeIcon />;
  };

  const planName = (agency?.subscription?.planName || agency?.subscriptionPlan || 'Free').toLowerCase();
  const isMobileOnlyPlan = planName === 'openly mobile';
  const isIndividualPlan = ['individual', 'elite individual'].includes(planName);
  const hasAttendeeAccess = ['professional', 'agency premium', 'starter', 'premium', 'enterprise', 'demo', 'individual', 'elite individual'].includes(planName);
  const hasTeamAccess = (!['free', 'agency free', 'individual', 'elite individual'].includes(planName)) || planName === 'demo' || planName === 'openly mobile';
  const hasIntegrationAccess = ['professional', 'agency premium', 'premium', 'enterprise', 'demo'].includes(planName);
  const hasCommissionAccess = ['professional', 'agency premium', 'premium', 'enterprise', 'demo'].includes(planName);
  const isAdmin = hasAdminAccess(user);

  const isIndividualPlanHiddenItem = (item: any) => {
    const s = (item?.path || item?.label || '').toString().toLowerCase();
    return s.includes('team') || s.includes('subscription') || s.includes('settings') ||
      s.includes('integration') || s.includes('3rd party') || s.includes('commission') || s.includes('cost items');
  };

  const mobileOnlyRootPaths = ['dashboard', 'market-campaign'];
  let displayMenuStructure = isMobileOnlyPlan
    ? {
      root: (menuStructure.root || []).filter((i: any) => mobileOnlyRootPaths.includes((i.path || i.label || '').toString().toLowerCase())),
      admin: (menuStructure.admin || []).filter((i: any) => ['team', 'settings', 'subscription'].includes((i.path || i.label || '').toString().toLowerCase())),
      Commission: [] as any[],
    }
    : menuStructure;

  if (isIndividualPlan) {
    const filterHidden = (items: any[]) => (items || []).filter((i: any) => !isIndividualPlanHiddenItem(i));
    displayMenuStructure = Object.fromEntries(
      Object.entries(displayMenuStructure).map(([k, v]) => [k, Array.isArray(v) ? filterHidden(v) : v])
    ) as typeof displayMenuStructure;
  }

  const renderMenuItem = (item: any, isSubmenu = false) => {
    const key = item.path || item.label;
    const fullPath = item.path.startsWith('/') ? item.path : `/agencies/${agencySlug}/${item.path}`;
    const isActive = location.pathname === fullPath;
    const label = (item?.label || item?.path || '').toString().toLowerCase();
    const requiresAttendees = label.includes('attendee');
    const requiresTeam = label.includes('team') && !label.includes('settings');
    const requiresIntegration = label.includes('integration');
    const requiresCommission = label.includes('commission') || label.includes('cost items') || item?.path === 'commission-report';
    const isEnabled =
      (!requiresAttendees || hasAttendeeAccess) &&
      (!requiresTeam || hasTeamAccess) &&
      (!requiresIntegration || hasIntegrationAccess) &&
      (!requiresCommission || hasCommissionAccess);
    const disabledMessage = requiresAttendees ? 'Upgrade to the Agency Premium plan to access Attendees.'
      : requiresTeam ? (['individual', 'elite individual'].includes(planName) ? 'Individual plan does not include team management.' : 'Upgrade from Free to invite team members.')
        : requiresIntegration ? (['individual', 'elite individual'].includes(planName) ? 'Individual plan does not include third-party integrations.' : '3rd Party Integrations are available on the Agency Premium plan.')
          : requiresCommission ? (['individual', 'elite individual'].includes(planName) ? 'Individual plan does not include commission features.' : 'Commission features are available on the Agency Premium plan.')
            : '';

    const content = (
      <ListItem
        button
        disabled={!isEnabled}
        onClick={isEnabled ? () => navigate(fullPath) : undefined}
        selected={isActive}
        sx={{
          pl: sidebarCollapsed ? 1.5 : (isSubmenu ? 4 : 1.5),
          pr: 1.5, py: 0.75, mx: 1, my: 0.25, borderRadius: 1.5,
          justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
          color: isActive ? palette.accent : palette.ink,
          fontSize: 13.5, fontWeight: isActive ? 600 : 500,
          '&.Mui-selected': { backgroundColor: palette.accentSoft, color: palette.accent },
          '&.Mui-selected .MuiListItemIcon-root': { color: palette.accent },
          '&:hover:not(.Mui-selected)': { backgroundColor: palette.surfaceMuted },
        }}
      >
        <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 'auto' : 36, color: isActive ? palette.accent : palette.inkMuted, '& svg': { fontSize: 20 } }}>
          {getIconForMenuItem(item.icon, item)}
        </ListItemIcon>
        {!sidebarCollapsed && <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13.5, fontWeight: 'inherit' }} />}
      </ListItem>
    );

    if (sidebarCollapsed) return <Tooltip key={key} title={disabledMessage || item.label} placement="right"><div>{content}</div></Tooltip>;
    return disabledMessage ? <Tooltip key={key} title={disabledMessage} placement="right"><div>{cloneElement(content, { key })}</div></Tooltip> : cloneElement(content, { key });
  };

  const renderMenuSection = (items: any[], groupLabel?: string) => {
    if (!items || items.length === 0) return null;
    return (
      <>
        {!sidebarCollapsed && groupLabel && (
          <Typography sx={{ px: 2.5, pt: 2, pb: 0.5, fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.inkSubtle, fontWeight: 600 }}>
            {groupLabel}
          </Typography>
        )}
        {items.map((item) => {
          const hasSubmenus = menuStructure[item.label] && menuStructure[item.label]!.length > 0;
          const isExpanded = expandedMenus[item.label] || false;
          if (hasSubmenus) {
            return (
              <div key={item.label}>
                <ListItem
                  button onClick={() => handleMenuToggle(item.label)}
                  sx={{ mx: 1, my: 0.25, borderRadius: 1.5, py: 0.75, color: palette.ink }}
                >
                  <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 'auto' : 36, color: palette.inkMuted, '& svg': { fontSize: 20 } }}>
                    {getIconForMenuItem(item.icon, item)}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }} />}
                  {!sidebarCollapsed && (isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)}
                </ListItem>
                {!sidebarCollapsed && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {menuStructure[item.label]!.filter((s: any) => !s.adminOnly || isAdmin).map((s: any) => renderMenuItem(s, true))}
                    </List>
                  </Collapse>
                )}
              </div>
            );
          }
          return renderMenuItem(item);
        })}
      </>
    );
  };

  if (loading || featuresLoading || !agencySlug) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
  }

  const drawer = (
    <div>
      {/* Brand block */}
      {agency?.bannerImageData && agency?.bannerImageType ? (
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', borderBottom: `1px solid ${palette.line}`, minHeight: sidebarCollapsed ? 64 : 88, p: 0 }}>
          {sidebarCollapsed ? (
            <BrandMark size={32} />
          ) : (
            <>
              <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', p: 1 }}>
                <img src={`data:${agency.bannerImageType};base64,${agency.bannerImageData}`} alt="Agency Banner"
                  style={{ width: '100%', height: 'auto', objectFit: 'contain', maxHeight: '88px' }} />
              </Box>
              {agency.bannerTitle && <Typography variant="subtitle2" sx={{ textAlign: 'center', px: 1, pb: 1 }}>{agency.bannerTitle}</Typography>}
            </>
          )}
        </Box>
      ) : (
        <Toolbar sx={{ borderBottom: `1px solid ${palette.line}`, gap: 1.25, minHeight: 64, px: 2 }}>
          <BrandMark size={32} />
          {!sidebarCollapsed && (
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 14.5, letterSpacing: '-0.01em', lineHeight: 1.1 }}>Workflow.Estate</Typography>
              <Typography sx={{ fontFamily: FONT_MONO, fontSize: 10, color: palette.inkMuted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{agency?.name || 'Workspace'}</Typography>
            </Box>
          )}
        </Toolbar>
      )}

      <List sx={{ pt: 1.5 }}>
        {displayMenuStructure.root && renderMenuSection(displayMenuStructure.root, 'Workspace')}

        {Object.entries(displayMenuStructure)
          .filter(([k]) => k !== 'root' && k !== 'Commission')
          .map(([parentKey, items]) => (
            <div key={parentKey}>{renderMenuSection(items as any[], parentKey)}</div>
          ))}

        {/* Support */}
        {sidebarCollapsed ? (
          <Tooltip title="Support" placement="right">
            <ListItem button onClick={() => navigate(`/agencies/${agencySlug}/support`)} selected={location.pathname === `/agencies/${agencySlug}/support`}
              sx={{ justifyContent: 'center', mx: 1, my: 0.25, borderRadius: 1.5, py: 0.75 }}>
              <ListItemIcon sx={{ minWidth: 'auto', color: palette.inkMuted }}><SupportIcon fontSize="small" /></ListItemIcon>
            </ListItem>
          </Tooltip>
        ) : (
          <ListItem button onClick={() => navigate(`/agencies/${agencySlug}/support`)} selected={location.pathname === `/agencies/${agencySlug}/support`}
            sx={{ mx: 1, my: 0.25, borderRadius: 1.5, py: 0.75 }}>
            <ListItemIcon sx={{ minWidth: 36, color: palette.inkMuted }}><SupportIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Support" primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }} />
          </ListItem>
        )}

        <ListItem button onClick={() => setLogoutDialogOpen(true)} sx={{ mx: 1, my: 0.25, borderRadius: 1.5, py: 0.75, justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
          <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 'auto' : 36, color: palette.inkMuted }}><LogoutIcon fontSize="small" /></ListItemIcon>
          {!sidebarCollapsed && <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }} />}
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', backgroundColor: palette.bg, minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${sidebarCollapsed ? miniWidth : drawerWidth}px)` },
          ml: { sm: `${sidebarCollapsed ? miniWidth : drawerWidth}px` },
          '@media (max-width:600px)': { width: '100%', ml: 0 },
          pt: 'env(safe-area-inset-top)',
          backgroundColor: palette.bg,
          color: palette.ink,
          borderBottom: `1px solid ${palette.line}`,
          boxShadow: 'none',
          backdropFilter: 'saturate(180%) blur(8px)',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 60 }, px: { xs: 1.5, sm: 3 } }}>
          {isMobile ? (
            <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 1 }} size="medium">
              <MenuIcon />
            </IconButton>
          ) : (
            <Tooltip title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="bottom">
              <IconButton onClick={handleDrawerToggle} size="small" sx={{ mr: 1.5, color: palette.inkMuted }}>
                {sidebarCollapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}

          {/* Breadcrumb-ish title (replaces "Workflow.Estate" repeated branding) */}
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 1 }}>
            <Typography sx={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: palette.inkSubtle }} noWrap>
              {agency?.name || 'Workspace'}
            </Typography>
            <Typography sx={{ color: palette.inkSubtle }}>/</Typography>
            <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: palette.ink }} noWrap>
              {(() => {
                const seg = location.pathname.split('/').filter(Boolean).pop() || 'Dashboard';
                return seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, ' ');
              })()}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 8 }} />

          {planName && !isMobile && (
            <Chip
              label={planName.charAt(0).toUpperCase() + planName.slice(1)}
              size="small"
              sx={{ mr: 2, bgcolor: palette.accentSoft, color: palette.accent, fontWeight: 600, fontSize: 11, height: 24, borderRadius: 999 }}
            />
          )}
          <NotificationBell agencySlug={agencySlug || ''} />
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', minWidth: 0, ml: 1, '&:hover': { opacity: 0.85 } }} onClick={handleProfileMenuOpen} aria-label="Open profile menu">
            <Avatar
              sx={{ width: 32, height: 32, bgcolor: palette.ink, color: '#fff', fontSize: 12, fontWeight: 600, mr: { xs: 0, sm: 1 } }}
              src={user && (user as any).avatarImageData && (user as any).avatarImageType ? `data:${(user as any).avatarImageType};base64,${(user as any).avatarImageData}` : undefined}
            >
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </Avatar>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 500 }} noWrap>
              {user?.firstName} {user?.lastName}
            </Typography>
          </Box>
          <Menu anchorEl={profileMenuAnchor} open={Boolean(profileMenuAnchor)} onClose={handleProfileMenuClose} onClick={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
            <MenuItem onClick={handleProfileClick}><ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon><ListItemText>Profile</ListItemText></MenuItem>
            <MenuItem onClick={() => setLogoutDialogOpen(true)}><ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon><ListItemText>Logout</ListItemText></MenuItem>
          </Menu>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1.5, flexShrink: 0 }} aria-label="Workflow.Estate">
            <BrandMark size={28} />
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: sidebarCollapsed ? miniWidth : drawerWidth }, flexShrink: 0, position: 'relative' }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: { sm: sidebarCollapsed ? miniWidth : drawerWidth },
              overflowX: 'hidden',
              transition: 'width 0.18s ease-in-out',
              borderRight: `1px solid ${palette.line}`,
              backgroundColor: palette.surface,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{
        flexGrow: 1, p: { xs: 2, sm: 3 },
        pb: { xs: 'max(16px, env(safe-area-inset-bottom))', sm: 3 },
        width: { sm: `calc(100% - ${sidebarCollapsed ? miniWidth : drawerWidth}px)` },
        '@media (max-width:600px)': { width: '100%' },
        mt: 'calc(60px + env(safe-area-inset-top, 0px))',
      }}>
        {children}
      </Box>

      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700 }}>Logout</DialogTitle>
        <DialogContent><Typography variant="body2">Are you sure you want to log out?</Typography></DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setLogoutDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => { setLogoutDialogOpen(false); logout(); }}>Log out</Button>
        </DialogActions>
      </Dialog>

      <PropertyAssistantChat />
    </Box>
  );
};

export default MainLayout;
